<template>
  <div class="target-lines">
    <el-card shadow="never">
      <div class="config-section">
        <el-form inline>
          <el-form-item label="年级">
            <el-select v-model="filters.grade" style="width: 120px">
              <el-option v-for="g in availableGrades" :key="g" :label="g" :value="g" />
            </el-select>
          </el-form-item>
        </el-form>
        
        <div class="target-lines-config">
          <div v-for="(line, index) in targetLines" :key="index" class="target-line-item">
            <el-input v-model="line.label" placeholder="线名称" style="width: 100px" />
            <el-input-number v-model="line.score" :min="0" :step="10" style="width: 120px" placeholder="分数" />
            <el-button type="danger" circle plain @click="removeLine(index)" v-if="targetLines.length > 1">
              <el-icon><Delete /></el-icon>
            </el-button>
          </div>
          <el-button type="dashed" @click="addLine" :icon="Plus">添加分数线</el-button>
        </div>

        <el-button type="primary" :icon="DataAnalysis" @click="fetchData" :loading="loading" :disabled="!semesterId || !examType">
          开始分析
        </el-button>
      </div>

      <div v-if="loading" style="padding: 20px;">
        <el-skeleton :rows="6" animated />
      </div>
      <el-empty v-else-if="!hasData" description="暂无上线率分析数据" />
      <div v-else class="result-section">
        <!-- 年级汇总 -->
        <div class="summary-cards">
          <div v-for="line in targetLines" :key="'sum-'+line.label" class="summary-card">
            <div class="summary-title">{{ line.label }} (≥{{ line.score }})</div>
            <div class="summary-data">
              <div class="data-item">
                <span class="label">达标人数</span>
                <span class="value">{{ summaryData.lines[line.label]?.passed || 0 }}</span>
              </div>
              <div class="data-item">
                <span class="label">上线率</span>
                <span class="value rate">{{ summaryData.lines[line.label]?.rate || 0 }}%</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 班级明细 -->
        <el-table :data="classData" border stripe style="width: 100%; margin-top: 20px" :header-cell-style="{background: '#f3f4f6', color: '#111827'}">
          <el-table-column prop="class_name" label="班级" width="120" fixed />
          <el-table-column prop="total" label="总人数" width="100" />
          
          <el-table-column v-for="line in targetLines" :key="'col-'+line.label" :label="line.label" align="center">
            <el-table-column label="上线人数" width="100" align="center">
              <template #default="{ row }">
                <strong style="color: #4f46e5">{{ row.lines[line.label]?.passed || 0 }}</strong>
              </template>
            </el-table-column>
            <el-table-column label="上线率" width="100" align="center">
              <template #default="{ row }">
                <span :class="row.lines[line.label]?.rate > 0 ? 'active-rate' : ''">
                  {{ row.lines[line.label]?.rate || 0 }}%
                </span>
              </template>
            </el-table-column>
          </el-table-column>
        </el-table>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, defineProps } from 'vue'
import { DataAnalysis, Plus, Delete } from '@element-plus/icons-vue'
import { gradeAnalysisApi } from '@/api'
import { ElMessage } from 'element-plus'

const props = defineProps({
  semesterId: { type: Number, required: false },
  examType: { type: String, required: false },
  availableGrades: { type: Array, default: () => [] }
})

const loading = ref(false)
const filters = reactive({
  grade: props.availableGrades[0] || ''
})

const targetLines = ref([
  { label: '特控线', score: 500 },
  { label: '本科线', score: 400 }
])

const classData = ref([])
const summaryData = ref({})

const hasData = computed(() => classData.value && classData.value.length > 0)

const addLine = () => {
  targetLines.value.push({ label: `目标线${targetLines.value.length + 1}`, score: 300 })
}

const removeLine = (index) => {
  targetLines.value.splice(index, 1)
}

const fetchData = async () => {
  if (!props.semesterId || !props.examType || !filters.grade) return
  
  // 过滤掉未输入的
  const validLines = targetLines.value.filter(l => l.label && l.score > 0)
  if (validLines.length === 0) {
    ElMessage.warning('请至少设置一条有效的分数线')
    return
  }

  loading.value = true
  try {
    const res = await gradeAnalysisApi.getTargetLinesAnalysis({
      semester_id: props.semesterId,
      exam_type: props.examType,
      grade: filters.grade,
      target_lines: validLines
    })
    
    classData.value = res.classes || []
    summaryData.value = res.summary || {}
    
    if (!hasData.value) {
      ElMessage.warning('该条件下无数据')
    }
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.config-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
}

.target-lines-config {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px dashed #d1d5db;
}

.target-line-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.summary-cards {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.summary-card {
  flex: 1;
  min-width: 200px;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  border-radius: 10px;
  padding: 16px;
  color: white;
}

.summary-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  opacity: 0.9;
}

.summary-data {
  display: flex;
  justify-content: space-between;
}

.data-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.data-item .label {
  font-size: 12px;
  opacity: 0.8;
}

.data-item .value {
  font-size: 24px;
  font-weight: 700;
}

.data-item .rate {
  color: #fbbf24;
}

.active-rate {
  font-weight: 600;
  color: #059669;
}
</style>
